/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Button, Checkbox, Empty, Spin, Typography, Alert } from 'antd';
import { useCallback, useRef, useState, Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  ZIP_IMPORT_SIZE_LIMIT_BYTES,
  readZipFolderPaths,
} from '~src/utils/zipImportUtils';

const { Title } = Typography;

interface ZipUploadStepProps {
  selectedPaths: Set<string>;
  onPathsChange: (paths: Set<string>) => void;
  onZipProcessed: (paths: string[]) => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
};

export const ZipUploadStep: React.FC<ZipUploadStepProps> = ({
  selectedPaths,
  onPathsChange,
  onZipProcessed,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [isReading, setIsReading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [folderPaths, setFolderPaths] = useState<string[]>([]);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(
    async (file: File) => {
      // Validate file type
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.zip'];
      const hasValidExtension = validExtensions.some((ext) =>
        fileName.endsWith(ext)
      );

      if (
        !hasValidExtension &&
        file.type !== 'application/zip' &&
        file.type !== 'application/x-zip-compressed'
      ) {
        // Check if it's an unsupported archive format
        const unsupportedExtensions = ['.7z', '.rar', '.tar', '.gz', '.bz2'];
        const isUnsupportedArchive = unsupportedExtensions.some((ext) =>
          fileName.endsWith(ext)
        );

        if (isUnsupportedArchive) {
          setError(intl.formatMessage({ id: 'invalid-archive-format' }));
        } else {
          setError(intl.formatMessage({ id: 'import-zip-not-zip-error' }));
        }
        return;
      }

      // Validate file size
      if (file.size > ZIP_IMPORT_SIZE_LIMIT_BYTES) {
        setError(
          intl.formatMessage(
            { id: 'import-zip-size-error' },
            { limit: formatBytes(ZIP_IMPORT_SIZE_LIMIT_BYTES) }
          )
        );
        return;
      }

      setError(null);
      setIsReading(true);

      try {
        const paths = await readZipFolderPaths(file);

        if (paths.length === 0) {
          setError(intl.formatMessage({ id: 'import-zip-no-folders' }));
          setIsReading(false);
          return;
        }

        setFolderPaths(paths);
        const allPathsSet = new Set(paths);
        onPathsChange(allPathsSet);
        onZipProcessed(paths);

        // Expand all folders by default
        setExpandedPaths(allPathsSet);
      } catch (err) {
        console.error('Error reading ZIP:', err);
        setError(intl.formatMessage({ id: 'generic-error' }));
      } finally {
        setIsReading(false);
      }
    },
    [intl, onPathsChange, onZipProcessed]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const togglePath = (path: string) => {
    const next = new Set(selectedPaths);
    if (next.has(path)) {
      // Deselect this path and all its children
      Array.from(next).forEach((p: string) => {
        if (p === path || p.startsWith(path + '/')) next.delete(p);
      });
    } else {
      // Select this path and all its children
      next.add(path);
      // Find and add all children paths
      folderPaths.forEach((p) => {
        if (p.startsWith(path + '/')) {
          next.add(p);
        }
      });
    }
    onPathsChange(next);
  };

  const selectAll = () => onPathsChange(new Set(folderPaths));
  const deselectAll = () => onPathsChange(new Set());

  const handleExpandAll = () => setExpandedPaths(new Set(folderPaths));
  const handleCollapseAll = () => setExpandedPaths(new Set());

  const toggleExpand = (path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  // Build tree structure from flat paths
  const folderTree = useMemo(() => {
    const tree: FolderTreeNode[] = [];
    const pathMap = new Map<string, FolderTreeNode>();

    folderPaths.forEach((path) => {
      const segments = path.split('/');
      const name = segments[segments.length - 1];
      const node: FolderTreeNode = {
        path,
        name,
        children: [],
      };
      pathMap.set(path, node);

      // Find parent
      if (segments.length === 1) {
        tree.push(node);
      } else {
        const parentPath = segments.slice(0, -1).join('/');
        const parent = pathMap.get(parentPath);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    return tree;
  }, [folderPaths]);

  if (folderPaths.length === 0) {
    return (
      <div css={styles.container}>
        <Title level={4} css={styles.sectionTitle}>
          {intl.formatMessage({ id: 'upload-zip-template' })}
        </Title>

        <Alert
          message={intl.formatMessage({ id: 'zip-folders-only-info' })}
          type="info"
          showIcon
          css={styles.infoAlert}
        />

        <div css={styles.pickContainer}>
          <div
            css={[styles.dropZone, dragOver && styles.dropZoneOver]}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}>
            {isReading ? (
              <div css={styles.readingContainer}>
                <Spin />
                <Typography.Text css={styles.readingText}>
                  {intl.formatMessage({ id: 'import-zip-reading' })}
                </Typography.Text>
              </div>
            ) : (
              <div css={styles.dropContent}>
                <FontAwesomeIcon
                  icon={['far', 'file-archive']}
                  css={styles.dropIcon}
                />
                <Typography.Text strong css={styles.dropLabel}>
                  {intl.formatMessage({ id: 'import-zip-drop-label' })}
                </Typography.Text>
                <Typography.Text css={styles.dropHint}>
                  {intl.formatMessage(
                    { id: 'import-zip-size-hint' },
                    { limit: formatBytes(ZIP_IMPORT_SIZE_LIMIT_BYTES) }
                  )}
                </Typography.Text>
              </div>
            )}
          </div>

          {error && (
            <div css={styles.errorBlock}>
              <FontAwesomeIcon
                icon={['fas', 'exclamation-circle']}
                css={styles.errorIcon}
              />
              <Typography.Text css={styles.errorText}>{error}</Typography.Text>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            css={styles.hiddenInput}
            onChange={handleFileInput}
          />
        </div>
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <Title level={4} css={styles.sectionTitle}>
        {intl.formatMessage({ id: 'import-zip-select-folders' })}
      </Title>

      <div css={styles.buttonRow}>
        <div css={styles.leftButtons}>
          <Button size="small" onClick={selectAll} css={styles.actionButton}>
            {intl.formatMessage({ id: 'select-all-folders' })}
          </Button>
          <Button size="small" onClick={deselectAll} css={styles.actionButton}>
            {intl.formatMessage({ id: 'clear-selection' })}
          </Button>
        </div>
        <div css={styles.rightButtons}>
          <Button
            size="small"
            onClick={handleExpandAll}
            css={styles.actionButton}>
            {intl.formatMessage({ id: 'expand-all' })}
          </Button>
          <Button
            size="small"
            onClick={handleCollapseAll}
            css={styles.actionButton}>
            {intl.formatMessage({ id: 'collapse-all' })}
          </Button>
        </div>
      </div>

      <div css={styles.folderList}>
        {folderTree.length === 0 ? (
          <Empty
            description={intl.formatMessage({ id: 'import-zip-no-folders' })}
          />
        ) : (
          folderTree.map((node) => (
            <ZipFolderTreeNode
              key={node.path}
              node={node}
              level={0}
              selectedPaths={selectedPaths}
              expandedPaths={expandedPaths}
              onTogglePath={togglePath}
              onToggleExpand={toggleExpand}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface FolderTreeNode {
  path: string;
  name: string;
  children: FolderTreeNode[];
}

interface ZipFolderTreeNodeProps {
  node: FolderTreeNode;
  level: number;
  selectedPaths: Set<string>;
  expandedPaths: Set<string>;
  onTogglePath: (path: string) => void;
  onToggleExpand: (path: string) => void;
}

const ZipFolderTreeNode: React.FC<ZipFolderTreeNodeProps> = ({
  node,
  level,
  selectedPaths,
  expandedPaths,
  onTogglePath,
  onToggleExpand,
}) => {
  const styles = useStyles(styleFn);

  const isSelected = selectedPaths.has(node.path);
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedPaths.has(node.path);

  return (
    <div css={styles.treeItem}>
      <div css={[styles.item, { paddingLeft: level * 20 + 12 }]}>
        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onChange={() => onTogglePath(node.path)}
          css={styles.checkbox}
        />

        {/* Chevron container - always present for alignment */}
        <div css={styles.chevronContainer}>
          {hasChildren && (
            <FontAwesomeIcon
              icon={['fas', isExpanded ? 'chevron-down' : 'chevron-right']}
              css={styles.chevronIcon}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(node.path);
              }}
            />
          )}
        </div>

        {/* Folder icon */}
        <FontAwesomeIcon
          icon={['fas', 'folder']}
          css={[styles.folderIcon, isSelected && styles.folderIconSelected]}
        />

        {/* Folder name */}
        <span css={styles.folderName} onClick={() => onTogglePath(node.path)}>
          {node.name}
        </span>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div css={styles.children}>
          {node.children.map((child) => (
            <ZipFolderTreeNode
              key={child.path}
              node={child}
              level={level + 1}
              selectedPaths={selectedPaths}
              expandedPaths={expandedPaths}
              onTogglePath={onTogglePath}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 0,
    color: theme.colors.text.primary,
    fontWeight: 600,
  },
  infoAlert: {
    marginBottom: 8,
  },
  pickContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    border: `2px dashed ${theme.colors.lightGray}`,
    borderRadius: 8,
    padding: '36px 24px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: theme.colors.primary,
      background: 'rgba(0,0,0,0.01)',
    },
  },
  dropZoneOver: {
    borderColor: theme.colors.primary,
    background: 'rgba(0,0,0,0.02)',
  },
  dropContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  dropIcon: {
    fontSize: 48,
    color: theme.colors.primary,
    opacity: 0.7,
  },
  dropLabel: {
    fontSize: 15,
    color: theme.colors.text.primary,
  },
  dropHint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
  },
  readingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: 12,
  },
  readingText: {
    color: theme.colors.text.secondary,
    fontSize: 13,
  },
  errorBlock: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '10px 14px',
    background: '#fff2f0',
    border: '1px solid #ffccc7',
    borderRadius: 6,
  },
  errorIcon: {
    color: '#ff4d4f',
    marginTop: 2,
    flexShrink: 0,
  },
  errorText: {
    color: '#cf1322',
    fontSize: 13,
  },
  hiddenInput: {
    display: 'none',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  leftButtons: {
    display: 'flex',
    gap: 8,
  },
  rightButtons: {
    display: 'flex',
    gap: 8,
  },
  actionButton: {
    borderRadius: 6,
    fontWeight: 500,
    fontSize: 13,
    height: 30,
    padding: '0 12px',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text.primary,
    background: '#ffffff',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: theme.colors.primary,
      color: theme.colors.primary,
    },
  },
  folderList: {
    maxHeight: 380,
    overflowY: 'auto',
    border: `1px solid ${theme.colors.lightGray}`,
    borderRadius: 6,
    padding: '8px',
  },
  treeItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 12px',
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 6,
    transition: 'all 0.2s ease',
    marginBottom: 8,
    '&:hover': {
      backgroundColor: theme.colors.primaryLight,
      borderColor: theme.colors.primary,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
  },
  chevronContainer: {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  chevronIcon: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    flexShrink: 0,
    '&:hover': {
      color: theme.colors.primary,
    },
  },
  checkbox: {
    flexShrink: 0,
  },
  folderIcon: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    flexShrink: 0,
    transition: 'color 0.2s ease',
  },
  folderIconSelected: {
    color: theme.colors.primary,
  },
  folderName: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.text.primary,
    cursor: 'pointer',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  children: {
    marginLeft: 20,
    display: 'flex',
    flexDirection: 'column',
  },
});
